import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {EyeDropperIconCTASize} from '@Components/eyedropper-color-picker/eyedropper-color-picker';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {RGBA} from '@Utils/color.util';
import {deviceType, isAndroid, isFirefox, isIOS, isMacOs, isOpera, isSafari, isSamsungBrowser} from 'react-device-detect';
import Mousetrap from 'mousetrap';
import {Canvas} from '@postermywall/fabricjs-2';
import styles from './canvas-color-picker.module.scss';
import {CURSOR_OVERLAY_CONTAINER_ID} from '@Components/poster-editor/components/cursor-overlay/cursor-overlay.types';
import type {CanvasEvents, Point} from '@postermywall/fabricjs-2';
import {applyCanvasCursor} from '@Utils/setCanvasCursor';

const DEVICE_TYPE = {
  MOBILE: 'mobile',
};

const CURSOR_OFFSET = 45;

interface CanvasColorPickerProps {
  color: RGBA;
  onChangeColor(color: RGBA | null): void;
  onChangeEnded(color: RGBA | null): void;
  className?: string;
  /** COMPACT is the design system's 32×32 square icon button, matching XSMALL input fields (as for the native eyedropper). */
  iconCtaSize?: EyeDropperIconCTASize;

  onChangeColorPickerMode?(state: boolean): void;
}

const getAllFabricCanvases = (): Canvas[] => {
  const pages = window.posterEditor?.whiteboard?.pages.getPagesInOrder() ?? [];
  return pages.map((page) => page.fabricCanvas).filter((canvas): canvas is Canvas => canvas instanceof Canvas);
};

const getCanvasFromEvent = (e: CanvasEvents['mouse:move']): Canvas | null => {
  const nativeTarget = e.e.target;
  if (!(nativeTarget instanceof HTMLCanvasElement)) {
    return null;
  }

  const canvases = getAllFabricCanvases();
  for (const canvas of canvases) {
    if (canvas.upperCanvasEl === nativeTarget || canvas.getElement() === nativeTarget) {
      return canvas;
    }
  }

  return null;
};

export function CanvasColorPicker({
  className = '',
  onChangeColorPickerMode = (): void => {},
  iconCtaSize = EyeDropperIconCTASize.DEFAULT,
  ...props
}: CanvasColorPickerProps): ReactElement {
  const [isColorPickerEnabled, setIsColorPickerEnabled] = useState(false);
  const colorBeforePickerIsEnabled = useRef<RGBA>(undefined);
  // Read by the unmount cleanup, which closes over the first render's state and so can't see the
  // latest isColorPickerEnabled.
  const isColorPickModeActiveRef = useRef(false);
  const isEscapeBoundRef = useRef(false);
  const pickPointerDownListenerRef = useRef<((event: PointerEvent) => void) | null>(null);

  const onClickPicker = (): void => {
    if (!isColorPickerEnabled) {
      enableColorPicker();
      setIsColorPickerEnabled(true);
      onChangeColorPickerMode(true);
    } else {
      disableColorPickMode();
    }
  };

  const isCustomCursorPositioningUnavailable = (): boolean => {
    return (isSafari && (isIOS || isMacOs)) || isSamsungBrowser || isOpera || (isFirefox && isAndroid);
  };

  const getColorFromCoOrdinates = (e: CanvasEvents['mouse:move']): RGBA | null => {
    const sourceCanvas = e.target?.canvas ?? getCanvasFromEvent(e);
    if (!sourceCanvas) {
      return null;
    }

    return getColorAtViewportPoint(sourceCanvas, e.viewportPoint);
  };

  const getColorAtViewportPoint = (sourceCanvas: Canvas, pointer: Point): RGBA => {
    const retinaScaling = sourceCanvas.getRetinaScaling();
    const p = sourceCanvas
      .getContext()
      .getImageData(pointer.x * retinaScaling, pointer.y * retinaScaling + (isCustomCursorPositioningUnavailable() ? CURSOR_OFFSET : 0), 1, 1).data;
    return [p[0], p[1], p[2], 1];
  };

  const onColorPicking = (e: CanvasEvents['mouse:move']): void => {
    const color = getColorFromCoOrdinates(e);
    props.onChangeColor(color);
  };

  /**
   * The pick click is consumed on the canvas wrapper in the capture phase, before fabric sees it: otherwise the same click
   * also selects (or deselects) whatever sits under the cursor, which swaps the panel and closes the colour popover.
   */
  const onPickPointerDown = (event: PointerEvent): void => {
    const sourceCanvas = getAllFabricCanvases().find((canvas) => {
      return canvas.wrapperEl === event.currentTarget;
    });
    if (!sourceCanvas) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    props.onChangeEnded(getColorAtViewportPoint(sourceCanvas, sourceCanvas.getViewportPoint(event)));
    disableColorPickMode();
  };

  const onRevertColor = (): void => {
    if (colorBeforePickerIsEnabled.current) {
      props.onChangeColor(colorBeforePickerIsEnabled.current);
    }
  };

  const disableColorPickMode = (): void => {
    detachColorPickListeners();
    setIsColorPickerEnabled(false);
    onChangeColorPickerMode(false);
  };

  /**
   * Releases everything pick mode holds outside React: the fabric listeners, the cursor, the
   * document-level overlay listener and the escape binding. Split out of disableColorPickMode so
   * unmount can run it without touching state on a component that is going away.
   */
  const detachColorPickListeners = (): void => {
    for (const canvas of getAllFabricCanvases()) {
      canvas.off('mouse:move', onColorPicking);
      if (pickPointerDownListenerRef.current) {
        canvas.wrapperEl.removeEventListener('pointerdown', pickPointerDownListenerRef.current, {capture: true});
      }
      canvas.off('mouse:out', onRevertColor);
      applyCanvasCursor(canvas, 'default');
    }

    const cursorOverlay = document.getElementById(CURSOR_OVERLAY_CONTAINER_ID);
    cursorOverlay?.removeEventListener('click', disableColorPickMode);
    if (isEscapeBoundRef.current) {
      Mousetrap.unbind('escape');
      isEscapeBoundRef.current = false;
    }

    pickPointerDownListenerRef.current = null;
    isColorPickModeActiveRef.current = false;
  };

  useEffect(() => {
    return (): void => {
      if (isColorPickModeActiveRef.current) {
        detachColorPickListeners();
        // The editor-wide picking flag lives outside this component; left on, it keeps popovers ignoring outside clicks.
        onChangeColorPickerMode(false);
      }
    };
  }, []);

  const onPressEscapeOverloadKey = (): void => {
    disableColorPickMode();
    onRevertColor();
  };

  const enableColorPicker = (): void => {
    colorBeforePickerIsEnabled.current = props.color;
    const colorPickerCursor = `url("${window.PMW.util.asset_url('images/postermaker/color-picker.ico')}") 0 25 , auto`;

    const canvases = getAllFabricCanvases();
    pickPointerDownListenerRef.current = onPickPointerDown;
    for (const canvas of canvases) {
      applyCanvasCursor(canvas, colorPickerCursor);
      canvas.on('mouse:move', onColorPicking);
      canvas.wrapperEl.addEventListener('pointerdown', onPickPointerDown, {capture: true});
      canvas.on('mouse:out', onRevertColor);
    }

    const cursorOverlay = document.getElementById(CURSOR_OVERLAY_CONTAINER_ID);
    cursorOverlay?.addEventListener('click', disableColorPickMode);

    if (deviceType !== DEVICE_TYPE.MOBILE) {
      Mousetrap.bind('escape', onPressEscapeOverloadKey);
      isEscapeBoundRef.current = true;
    }

    isColorPickModeActiveRef.current = true;
  };

  return (
    <Icon
      onClick={onClickPicker}
      className={`${iconCtaSize === EyeDropperIconCTASize.COMPACT ? styles.compactPickerIcon : styles.pickerIcon} spacing-m-l-2`}
      icon="icon-colorpicker"
      size={iconCtaSize === EyeDropperIconCTASize.COMPACT ? IconSize.SIZE_ICON_16 : IconSize.SIZE_ICON_20}
      shape={IconShape.SQUARE}
      type={IconType.SECONDARY}
    />
  );
}
